/// <reference types="vite/client" />
import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  from,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { sha256 } from 'crypto-hash';
import { TypedTypePolicies } from 'lib-knowledge-management-common/graphql';
import { createAuthLink } from 'lib-knowledge-utils/apollo';
import { config } from 'lib-shared-backend-config';

export const version = 'beta';

function getManagementHttpLink() {
  return createHttpLink({
    uri: `${config.url.knowledgeApi}/management/api/${version}/graphql`,
  });
}

/** Exported so tests can build a cache that behaves like the real one. */
export const typePolicies: TypedTypePolicies = {
  Article: {
    fields: {
      tags: {
        merge(existing, incoming) {
          return incoming;
        },
      },
    },
  },

  Collection: {
    merge: true,
  },

  ArticleTranslation: {
    // @ts-expect-error doesn't like __typename
    keyFields: ['articleId', 'languageId', 'version', ['__typename']],
  },

  ArticleList: {
    keyFields: ['query', ['results']],
  },

  ArticleListItem: {
    fields: {
      titles: {
        merge(existing, incoming) {
          return incoming;
        },
      },
    },
  },

  Query: {
    fields: {
      category: {
        // How to look up a Category from the cache.
        read(_, { args, toReference }) {
          if (args?.id) {
            return toReference({
              __typename: 'Category',
              id: args.id,
            });
          }
        },
      },

      article: {
        // How to look up an Article from the cache.
        read(_, { args, toReference }) {
          if (args?.id) {
            return toReference({
              __typename: 'Article',
              id: args.id,
            });
          }
        },
      },

      aiDraft: {
        // How to look up an AiDraft from the cache. Without this, `cache-first`
        // on `aiDraft(id)` can never hit: the list writes the `AiDraft` entity
        // but no `ROOT_QUERY.aiDraft({"id":…})` field, so every selection would
        // go to the network and flash a loader over a draft already in hand.
        read(_, { args, toReference }) {
          if (args?.id) {
            return toReference({
              __typename: 'AiDraft',
              id: args.id,
            });
          }
        },
      },

      articles: {
        keyArgs: ['$filters', '$query'],
      },

      collections: {
        // One list across pages: `fetchMore` appends to it, and the cache reads
        // and writes that pass no variables still land on it.
        keyArgs: false,
        merge(existing, incoming, { args }) {
          if (!existing || !args?.offset) return incoming;
          return {
            ...incoming,
            results: [
              ...existing.results.slice(0, args.offset),
              ...incoming.results,
            ],
          };
        },
      },
    },
  },
};

export const cache = new InMemoryCache({
  typePolicies,
});

export let managementClient: ApolloClient<NormalizedCacheObject>;

const shouldUsePersistedQueries = import.meta.env.VITE_PERSISTED_QUERY !== '0';

export function createManagementApolloClient(): typeof managementClient {
  if (!managementClient) {
    const links = from(
      [
        shouldUsePersistedQueries
          ? createPersistedQueryLink({ sha256, useGETForHashedQueries: false })
          : undefined,
        createAuthLink(),
        getManagementHttpLink(),
      ].filter(Boolean) as ApolloLink[],
    );

    managementClient = new ApolloClient<NormalizedCacheObject>({
      link: links,
      cache,
      // NOTE: make sure cache updates are immutable or this fails!
      assumeImmutableResults: true,
      defaultOptions: {
        query: {
          canonizeResults: true,
        },
      },
      connectToDevTools: true,
    });
  }
  return managementClient;
}
